import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Tractor, Wrench, PackageSearch, Wallet, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ username: "", password: "" });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.username, form.password);
      toast.success("Giriş başarılı");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen tp-hero grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-10 bg-[#1B4D3E] text-white relative overflow-hidden">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#D97706] text-white grid place-items-center font-extrabold text-lg">TP</div>
            <div>
              <div className="text-2xl font-extrabold tracking-tight">TraktörPro</div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-200 font-bold">Bayi · Servis · Stok</div>
            </div>
          </div>
          <h1 className="mt-16 text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">
            Tarlanın nabzını<br />ofisinden yönet.
          </h1>
          <p className="mt-4 text-emerald-100/90 text-base max-w-md">
            Sıfır ve ikinci el traktör stoğu, yedek parça deposu, servis iş emirleri, teklif
            ve günlük tahsilat — hepsi tek panelde.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-3 max-w-md">
            {[
              { i: Tractor, t: "Sıfır & 2.El Stok" },
              { i: Wrench, t: "Servis İş Emri" },
              { i: PackageSearch, t: "Yedek Parça" },
              { i: Wallet, t: "Gelir/Gider" },
            ].map((f, idx) => (
              <div key={idx} className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
                <f.i size={18} className="text-[#F59E0B]" />
                <span className="text-sm font-medium">{f.t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-emerald-200/70">© {new Date().getFullYear()} TraktörPro · Türkiye</div>
        <div className="pointer-events-none absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-[#D97706]/25 blur-3xl" />
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md border-slate-200 shadow-xl">
          <CardContent className="p-6 sm:p-8">
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="h-10 w-10 rounded-xl bg-[#1B4D3E] text-white grid place-items-center font-bold">TP</div>
              <div>
                <div className="font-extrabold text-slate-900">TraktörPro</div>
                <div className="text-[11px] uppercase tracking-widest text-[#1B4D3E] font-bold">Bayi & Servis</div>
              </div>
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Hoşgeldiniz</h2>
            <p className="text-sm text-slate-500 mt-1">Devam etmek için hesabınıza giriş yapın.</p>

            <form onSubmit={submit} className="mt-6 space-y-3.5" data-testid="login-form">
              <div>
                <Label>Kullanıcı Adı</Label>
                <Input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="admin"
                  data-testid="input-username"
                  required
                  autoComplete="username"
                />
              </div>
              <div>
                <Label>Şifre</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="input-password"
                  required
                  autoComplete="current-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#1B4D3E] hover:bg-[#163e32] text-white h-11 mt-2"
                disabled={busy}
                data-testid="submit-auth"
              >
                {busy ? "İşleniyor…" : "Giriş Yap"}
              </Button>
            </form>

            <div className="mt-6 border border-dashed border-slate-200 rounded-xl p-3 bg-emerald-50/60">
              <div className="flex items-start gap-2">
                <ShieldCheck size={16} className="text-[#1B4D3E] mt-0.5" />
                <div>
                  <div className="text-[11px] uppercase tracking-widest font-bold text-[#1B4D3E]">Güvenli Erişim</div>
                  <div className="text-sm text-slate-700 mt-1">
                    Yeni kullanıcılar sadece yönetici tarafından oluşturulur. Yönetici hesabınız yoksa sistem yöneticiniz ile iletişime geçin.
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
